import { useCallback, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import { Movie } from 'src/@types/Movie';
import { FULL_MODE, PPS_PURCHASED, REGULAR_GUEST_MODE, SS_GUEST_MODE } from 'src/client/helpers/constants';
import { sendPlayedMovie } from 'src/client/redux/modules/playedMovies';
import { RESUMABLE_VIDEOS_COOKIE, TEASER_PLAYED_COOKIE } from 'src/shared/constants/cookies';

export type AccessMode =
  | null
  | typeof FULL_MODE
  | typeof REGULAR_GUEST_MODE
  | typeof SS_GUEST_MODE
  | typeof PPS_PURCHASED;

export type Videos = {
  loaded: boolean;
  loading: boolean;
  itemUUID?: string;
  videos: string[];
};

type JWPlayerOptions = {
  item: Movie;
  accessMode: AccessMode;
  skipM3U8: boolean;
  token: string;
  videos: Videos;
  contentCdnUrl: string;
  showSfwSite: boolean;
};

export const getJWPlayerOptions = ({
  item,
  accessMode,
  skipM3U8,
  token,
  videos,
  contentCdnUrl,
  showSfwSite,
}: JWPlayerOptions) => {
  let isTeaser = false;
  if (item.files.teasers.length > 0 && !(accessMode === FULL_MODE || accessMode === PPS_PURCHASED)) {
    isTeaser = true;
  }

  let useM3U8 = true;
  const iOSvers = iOSversion();
  const smartTV = isSmartTV();
  if ((iOSvers && iOSvers[0] <= 10) || smartTV || (isTeaser && !item.files.teasers.includes('m3u8'))) {
    useM3U8 = false;
  }

  const sources: {
    default: boolean;
    file: string;
    type: string;
    label: string;
  }[] = [];
  if (useM3U8 && !skipM3U8) {
    sources.push({
      default: true,
      file: `/api/m3u8/${item.UUID}.m3u8${token ? `?token=${token}` : ''}`,
      type: 'hls',
      label: '0',
    });
  }

  videos.videos.forEach((url, i) =>
    sources.push({
      default: i === 0 && (!useM3U8 || !!skipM3U8),
      label: `${i + 1}`,
      type: 'mp4',
      file: url,
    }),
  );

  const [movieCover] = getMovieCover({ showSfwSite, item, contentCdnUrl });

  return [
    {
      tracks: [
        {
          file: `${contentCdnUrl}/${item.siteUUID}/media/${item.UUID}/${isTeaser ? 'tease' : 'member'}_${
            item.UUID
          }.vtt`,
          kind: 'thumbnails',
        },
        ...(!isTeaser
          ? [
              {
                file: `${contentCdnUrl}/${item.siteUUID}/media/${item.UUID}/subtitles_${item.UUID}.xml`,
                label: 'English',
                kind: 'captions',
                default: true,
              },
            ]
          : []),
      ],
      sources,
      image: movieCover,
    },
  ];
};

export const useGenerateJWPlayerEvents = ({ accessMode, item, skipM3U8, setSkipM3U8 }) => {
  const [autoPlay, setAutoPlay] = useState(false);

  const dispatch = useDispatch();
  const [cookies, setCookie] = useCookies();
  const resumableVideosCookie = cookies[RESUMABLE_VIDEOS_COOKIE];
  const teaserPlayedCookie = cookies[TEASER_PLAYED_COOKIE];

  const generateEvents = useCallback(() => {
    if (!item) return {};
    const {
      UUID,
      siteUUID,
      name,
      path,
      models,
      photographers,
      publishedAt,
      isStaffSelection,
      isIntimateSelection,
    } = item;

    const onPlay = () => {
      let isTeaser = false;
      if (item.files.teasers.length > 0 && accessMode !== FULL_MODE && accessMode !== PPS_PURCHASED) {
        isTeaser = true;
      }

      if (isTeaser && !teaserPlayedCookie) {
        setTimeout(() => {
          setCookie(TEASER_PLAYED_COOKIE, 'true', {
            path: '/',
          });
        }, 6000);
      }

      if (accessMode !== FULL_MODE && accessMode !== PPS_PURCHASED) {
        return;
      }

      window.mixpanel.track('Video Play', {
        name,
        path,
        models: models.map((model) => model.name),
        photographers: photographers.map((photographer) => photographer.name),
        accessMode,
        publishedAt,
        isStaffSelection,
      });
      dispatch(sendPlayedMovie({ galleryUUID: UUID, siteUUID }));
    };

    const onReady = () => {
      const player = window.jwplayer('my-unique-id');

      if (accessMode !== FULL_MODE && accessMode !== PPS_PURCHASED) {
        if (teaserPlayedCookie) {
          player.seek(6);
          player.stop();
        }

        return;
      }

      const resumable = resumableVideosCookie;
      let shouldResume = true;

      if (!resumable || !resumable[UUID]) {
        shouldResume = false;
      }

      if (shouldResume) {
        const [resumeAt, duration] = resumable[UUID].split(':');
        if (parseInt(resumeAt) < parseInt(duration)) {
          player.seek(parseInt(resumeAt));
          player.stop();
        }
      }

      if (skipM3U8 && autoPlay) {
        player.play();
      }
    };

    const onTime = (e) => {
      if (accessMode !== FULL_MODE && accessMode !== PPS_PURCHASED) {
        return;
      }
      let resumable = resumableVideosCookie;
      const position = Math.floor(e.position);
      const duration = Math.floor(e.duration);

      if (!resumable) {
        resumable = {};
      }

      if (!resumable[UUID]) {
        resumable[UUID] = `0:${duration}`;
      }

      const [resumeAt] = resumable[UUID].split(':');

      if (position > parseInt(resumeAt)) {
        resumable[UUID] = `${position}:${duration}`;
        setCookie(RESUMABLE_VIDEOS_COOKIE, resumable, { path: '/' });
      }
    };

    const onError = (e) => {
      if (accessMode !== FULL_MODE && accessMode !== PPS_PURCHASED) {
        return;
      }
      const { code } = e;

      // 232600 -> HLS manifest (M3U8) could not be parsed
      // 224002 -> Unable to decode resource (M3U8 fix for Safari)
      window.mixpanel.track('Video Error', {
        name,
        path,
        models: models.map((model) => model.name),
        photographers: photographers.map((photographer) => photographer.name),
        accessMode,
        publishedAt,
        isStaffSelection,
        isIntimateSelection,
        errorCode: code,
      });

      if (code === 232600 || code === 224002) {
        const player = window.jwplayer('my-unique-id');
        player.stop();
        setSkipM3U8(true);
        setAutoPlay(true);
        setTimeout(player.play, 100);
      }
    };

    return {
      onPlay,
      onReady,
      onTime,
      onError,
    };
  }, [
    accessMode,
    autoPlay,
    dispatch,
    item,
    resumableVideosCookie,
    setCookie,
    setSkipM3U8,
    skipM3U8,
    teaserPlayedCookie,
  ]);

  return generateEvents;
};

type MovieCoverParams = {
  showSfwSite: boolean;
  item: Movie;
  contentCdnUrl: string;
};

export const getMovieCover = ({ showSfwSite, item, contentCdnUrl }: MovieCoverParams) => {
  const wideCover = showSfwSite && item.sfwFlags?.hasSfwWideCover ? item.splashImagePathSfw : item.splashImagePath;
  const portraitCover =
    showSfwSite && item.sfwFlags?.hasSfwPortraitCover ? item.coverImagePathSfw : item.coverImagePath;

  const useWideCover = item.files.sizes.relatedPhotos.some((photo) => photo.id === 'wide');

  const movieCover = useWideCover
    ? `${contentCdnUrl}/${item.siteUUID}${wideCover}`
    : `${contentCdnUrl}/${item.siteUUID}${portraitCover}`;

  const isSafe = useWideCover ? item.sfwFlags?.hasSfwWideCover : item.sfwFlags?.hasSfwPortraitCover;

  return [movieCover, isSafe] as const;
};

const iOSversion = () => {
  if (/iP(hone|od|ad)/.test(navigator.platform)) {
    const v = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (!v) {
      return false;
    }
    return [parseInt(v[1], 10), parseInt(v[2], 10), parseInt(v[3], 10) || 0];
  }
  return false;
};

const isSmartTV = () => /smarttv|PlayStation|PS4Application/.test(navigator.userAgent);
